import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { FileDownloadResponse } from './file-response';
const c = initContract();
export const ConsumptionAlertEventTypeSchema = z.enum([
    'WARNING_EXCEEDED',
    'FAIR_USE_EXCEEDED',
    'CUT_OFF_EXCEEDED',
    'CARDINALITY_EXCEEDED',
    'DATA_ROAMING_LOCKED',
]);
export const ConsumptionAlertSchema = z.object({
    id: z.string(),
    mobileLineId: z.string().nullable(),
    lineNumber: z.string(),
    eventType: ConsumptionAlertEventTypeSchema,
    usageCategory: z.string().nullable(),
    /** Line holder as named by the operator. */
    holderName: z.string().nullable(),
    /** Share of the plan reached, e.g. 82. Null when not computable. */
    percent: z.number().nullable(),
    /** Formatted quantity reached, e.g. "16,38 Go". */
    consumption: z.string().nullable(),
    /** Formatted threshold that fired the alert, e.g. "20 Go". */
    threshold: z.string().nullable(),
    occurredAt: z.string(),
});
export const ConsumptionAlertsPageSchema = z.object({
    alerts: z.array(ConsumptionAlertSchema),
    pageInfo: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
    }),
});
/**
 * One count per threshold, plus the overall total. `total` also covers the
 * alerts that map to no threshold, so it is not always the sum of the three.
 */
export const ConsumptionAlertSummarySchema = z.object({
    total: z.number(),
    warning: z.number(),
    fairUse: z.number(),
    cutOff: z.number(),
});
const listQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    /** Free-text search on the line number or the holder. */
    q: z.string().optional(),
    /** Inclusive occurredAt window (ISO dates). */
    from: z.string().optional(),
    to: z.string().optional(),
});
const summaryQuery = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
});
const orgParams = z.object({ organizationId: z.string().min(1) });
const siteParams = z.object({
    organizationId: z.string().min(1),
    siteId: z.string().min(1),
});
/** Same filters as the listing — the export mirrors what the user sees. */
export const ExportAlertsBodySchema = z.object({
    q: z.string().optional(),
    /** Inclusive occurredAt window (ISO dates). */
    from: z.string().optional(),
    to: z.string().optional(),
    format: z.enum(['xlsx', 'csv']),
    delimiter: z.string().optional(),
});
/**
 * Alerts are read organization-wide, or scoped to a billing centre when one
 * is selected. The scope sits in the path, like the lines listing.
 */
export const consumptionAlertContract = c.router({
    listAlerts: {
        method: 'GET',
        path: '/organizations/:organizationId/consumption-alerts',
        pathParams: orgParams,
        query: listQuery,
        responses: { 200: ConsumptionAlertsPageSchema },
        summary: 'Paginated consumption alerts of an organization',
    },
    getAlertSummary: {
        method: 'GET',
        path: '/organizations/:organizationId/consumption-alerts/summary',
        pathParams: orgParams,
        query: summaryQuery,
        responses: { 200: ConsumptionAlertSummarySchema },
        summary: 'Alert counts per threshold',
    },
    listSiteAlerts: {
        method: 'GET',
        path: '/organizations/:organizationId/sites/:siteId/consumption-alerts',
        pathParams: siteParams,
        query: listQuery,
        responses: { 200: ConsumptionAlertsPageSchema },
        summary: 'Paginated consumption alerts of a billing centre',
    },
    getSiteAlertSummary: {
        method: 'GET',
        path: '/organizations/:organizationId/sites/:siteId/consumption-alerts/summary',
        pathParams: siteParams,
        query: summaryQuery,
        responses: { 200: ConsumptionAlertSummarySchema },
        summary: 'Alert counts per threshold for a billing centre',
    },
    // POST like the other exports: the file is a generated artifact, not a listing.
    exportAlerts: {
        method: 'POST',
        path: '/organizations/:organizationId/consumption-alerts/export',
        pathParams: orgParams,
        body: ExportAlertsBodySchema,
        responses: { 200: FileDownloadResponse },
        summary: 'Export the consumption alerts as xlsx or csv',
    },
    exportSiteAlerts: {
        method: 'POST',
        path: '/organizations/:organizationId/sites/:siteId/consumption-alerts/export',
        pathParams: siteParams,
        body: ExportAlertsBodySchema,
        responses: { 200: FileDownloadResponse },
        summary: 'Export the consumption alerts of a billing centre as xlsx or csv',
    },
});
